import React, { useState, useEffect, useMemo, useRef } from "react";
import OperationsService from "./operationsService";
import { ColumnConfig, Waffles, WafflesPaginationProps } from "@app/shared/waffles";
import { FullJobDetailsByStatusResponse } from "./jobStatisticsResult";
import { FlyoutFooter } from "@app/shared/Flyouts";

interface FullJobDetailsFlyoutContentProps {
  clientId: number;
  runDate: string;
  jobStatus: string;
  lineOfBusiness: string;
  jobType: number;
}

const operationsService = new OperationsService();

export const FullJobDetailsFlyoutContent: React.FC<FullJobDetailsFlyoutContentProps> = ({
  clientId, runDate, jobStatus, lineOfBusiness, jobType
}) => {
  const [jobs, setJobs] = useState<FullJobDetailsByStatusResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [skip, setSkip] = useState(0);
  const take = useRef(20).current;

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const result = await operationsService.getFullJobDetailsByStatus(runDate, clientId, jobStatus, lineOfBusiness, jobType);
        setJobs(result);
      } catch (err) {
        setError("Error fetching job details");
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [clientId, runDate, jobStatus, lineOfBusiness, jobType]);

  const jobDetailsColumns: ColumnConfig = {
    clientId: {
      displayText: "Client ID",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_client-id-output",
    },
    countySSA: {
      displayText: "County SSA",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_county-ssa-output",
    },
    dateCancelled: {
      displayText: "Date Cancelled",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_date-cancelled-output",
    },
    dateCreated: {
      displayText: "Date Created",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_date-created-output",
    },
    dateFinished: {
      displayText: "Date Finished",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_date-finished-output",
    },
    dateQueued: {
      displayText: "Date Queued",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_date-queued-output",
    },
    dateQueuedForRetry: {
      displayText: "Date Queued for Retry",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_date-queued-for-retry-output",
    },
    dateStarted: {
      displayText: "Date Started",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_date-started-output",
    },
    errorMessage: {
      displayText: "Error Message",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_error-message-output",
    },
    jobId: {
      displayText: "Job ID",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_job-id-output",
    },
    jobQueueId: {
      displayText: "Job Queue ID",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_job-queue-id-output",
    },
    jobType: {
      displayText: "Job Type",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_job-type-output",
    },
    lastHeartbeatDate: {
      displayText: "Last Heartbeat Date",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_last-heartbeat-date-output",
    },
    lineOfBusiness: {
      displayText: "Line of Business",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_line-of-business-output",
    },
    messageSequenceNumber: {
      displayText: "Message Sequence Number",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_message-sequence-number-output",
    },
    payload: {
      displayText: "Payload",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_payload-output",
    },
    projectId: {
      displayText: "Project ID",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_project-id-output",
    },
    retryCount: {
      displayText: "Retry Count",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_retry-count-output",
    },
    success: {
      displayText: "Success",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_success-output",
    },
    userId: {
      displayText: "User ID",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_user-id-output",
    },
    workerRoleId: {
      displayText: "Worker Role ID",
      noSort: true,
      sizeContentToFit: true,
      testId: "weekend-run-monitoring_worker-role-id-output",
    },
  };

  const tableData = useMemo(() => {
    return jobs.map((job) => ({
      /* eslint-disable sort-keys */
      key: job.jobId.toString(),
      jobId: job.jobId,
      clientId: job.clientId,
      countySSA: job.countySSA,
      dateCancelled: job.dateCancelled ? new Date(job.dateCancelled).toLocaleDateString() : "N/A",
      dateCreated: new Date(job.dateCreated).toLocaleDateString(),
      dateFinished: job.dateFinished ? new Date(job.dateFinished).toLocaleDateString() : "N/A",
      dateQueued: job.dateQueued ? new Date(job.dateQueued).toLocaleDateString() : "N/A",
      dateQueuedForRetry: job.dateQueuedForRetry ? new Date(job.dateQueuedForRetry).toLocaleDateString() : "N/A",
      dateStarted: job.dateStarted ? new Date(job.dateStarted).toLocaleDateString() : "N/A",
      errorMessage: job.errorMessage || "N/A",
      jobQueueId: job.jobQueueId,
      jobType: job.jobType,
      lastHeartbeatDate: job.lastHeartbeatDate ? new Date(job.lastHeartbeatDate).toLocaleDateString() : "N/A",
      lineOfBusiness: job.lineOfBusiness,
      messageSequenceNumber: job.messageSequenceNumber,
      payload: job.payload || "N/A",
      projectId: job.projectId,
      retryCount: job.retryCount,
      success: job.success ? "Yes" : "No",
      userId: job.userId,
      workerRoleId: job.workerRoleId,
      /* eslint-enable sort-keys */
    }));
  }, [jobs]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => {
      setSkip((pageNumber - 1) * take);
    },
    recordsPerPage: take,
    skip,
    totalRecords: tableData.length,
  }

  return (
    <>
      <Waffles
        columns={jobDetailsColumns}
        data={tableData.slice(skip, skip + take)}
        enablePagination={true}
        // passThroughSorting={true}
        testId="full-job-details-table"
        paginationProps={paginationProps}
      />
      <FlyoutFooter
        submitButton={null}
      />
    </>
  );
};
